import { useEffect, useState } from "react";
import {
  Briefcase, CheckCircle2, Copy, Download, Edit3, FileText, Loader2, Plus, Search, Trash2, Users, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError, API_BASE } from "../../lib/api";

const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3 py-2 text-sm text-slate-200 outline-none focus:border-saffron";
const DEPARTMENTS = ["Operations", "Delivery", "Customer Support", "Sales", "Business Development", "Finance", "HR", "Technology", "Marketing", "Warehouse", "Management", "Other"];
const EMP_TYPES = ["Full Time", "Part Time", "Contract", "Internship", "Field", "Remote", "Hybrid"];
const APP_STATUS = ["new", "under_review", "shortlisted", "interview", "selected", "rejected"];
const APP_LABELS = { new: "New", under_review: "Under Review", shortlisted: "Shortlisted", interview: "Interview", selected: "Selected", rejected: "Rejected" };
const APP_COLORS = { new: "bg-saffron/15 text-saffron", under_review: "bg-blue-500/15 text-blue-400", shortlisted: "bg-purple-500/15 text-purple-400", interview: "bg-amber-500/15 text-amber-400", selected: "bg-emerald-500/15 text-emerald-400", rejected: "bg-red-500/15 text-red-400" };

const emptyJob = { title: "", department: "Operations", location: "", employment_type: "Full Time", experience: "", salary: "", show_salary: false, description: "", responsibilities: "", requirements: "", qualifications: "", skills: "", benefits: "", deadline: "", contact_email: "", featured: false, status: "draft" };
const lines = (s) => String(s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const toLines = (a) => (a || []).join("\n");

export default function AdminCareers() {
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState({ items: [], metrics: {} });
  const [talent, setTalent] = useState([]);
  const [cms, setCms] = useState(null);
  const [jobForm, setJobForm] = useState(null);
  const [openApp, setOpenApp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [jobFilter, setJobFilter] = useState({ status: "", q: "" });
  const [appFilter, setAppFilter] = useState({ status: "", q: "" });
  const [talentQ, setTalentQ] = useState("");

  const loadJobs = () => {
    const p = new URLSearchParams();
    if (jobFilter.status) p.set("status", jobFilter.status);
    if (jobFilter.q) p.set("q", jobFilter.q);
    api.get(`/careers/admin/jobs?${p}`).then((r) => setJobs(r.data)).catch(() => {});
  };
  const loadApps = () => {
    const p = new URLSearchParams();
    if (appFilter.status) p.set("status", appFilter.status);
    if (appFilter.q) p.set("q", appFilter.q);
    api.get(`/careers/admin/applications?${p}`).then((r) => setApps(r.data)).catch(() => {});
  };
  const loadTalent = () => api.get(`/careers/admin/talent?q=${encodeURIComponent(talentQ)}`).then((r) => setTalent(r.data)).catch(() => {});
  const loadCms = () => api.get("/careers/admin/cms").then((r) => setCms(r.data)).catch(() => {});
  useEffect(loadJobs, [jobFilter]);
  useEffect(loadApps, [appFilter]);
  useEffect(loadTalent, [talentQ]);
  useEffect(() => { loadCms(); }, []);

  const saveJob = async () => {
    setSaving(true);
    try {
      const payload = { ...jobForm,
        responsibilities: lines(jobForm.responsibilities), requirements: lines(jobForm.requirements),
        qualifications: lines(jobForm.qualifications), skills: String(jobForm.skills || "").split(",").map((s) => s.trim()).filter(Boolean),
        benefits: lines(jobForm.benefits) };
      delete payload.id; delete payload.job_id; delete payload.created_at; delete payload.applications;
      if (jobForm.id) await api.put(`/careers/admin/jobs/${jobForm.id}`, payload);
      else await api.post("/careers/admin/jobs", payload);
      toast.success(jobForm.id ? "Job updated" : "Job created");
      setJobForm(null); loadJobs();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const jobAction = async (j, action) => {
    try {
      if (action === "duplicate") { await api.post(`/careers/admin/jobs/${j.id}/duplicate`); toast.success("Duplicated as draft"); }
      else if (action === "delete") {
        if (!window.confirm(`Delete job "${j.title}"? Applications are kept but marked.`)) return;
        await api.delete(`/careers/admin/jobs/${j.id}`); toast.success("Job deleted");
      } else {
        await api.put(`/careers/admin/jobs/${j.id}`, { ...j, responsibilities: j.responsibilities || [], requirements: j.requirements || [], qualifications: j.qualifications || [], skills: j.skills || [], benefits: j.benefits || [], status: action });
        toast.success(`Job ${action}`);
      }
      loadJobs();
    } catch (err) { toast.error(apiError(err)); }
  };

  const setAppStatus = async (a, status) => {
    try { await api.put(`/careers/admin/applications/${a.id}`, { status }); toast.success(`Marked ${APP_LABELS[status]}`); loadApps(); setOpenApp({ ...a, status }); }
    catch (err) { toast.error(apiError(err)); }
  };

  const resumeUrl = (a) => `${API_BASE}/api/careers/admin/applications/${a.id}/resume`;
  const downloadResume = async (a) => {
    try {
      const r = await api.get(`/careers/admin/applications/${a.id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${a.application_id}_resume`;
      link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Resume not available"); }
  };

  const saveCms = async () => {
    setSaving(true);
    try { await api.put("/careers/admin/cms", cms); toast.success("Careers content saved"); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const TABS = [["jobs", "Jobs", Briefcase], ["applications", "Applications", FileText], ["talent", "Talent Community", Users], ["content", "Page Content", Edit3]];

  return (
    <div data-testid="admin-careers">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Careers Management</h1>
          <p className="mt-1 text-sm text-slate-500">Jobs, applications, talent community and careers page content.</p>
        </div>
        {tab === "jobs" && (
          <button onClick={() => setJobForm({ ...emptyJob })} data-testid="careers-new-job-btn"
                  className="flex items-center gap-2 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover">
            <Plus className="h-3.5 w-3.5" /> Create Job
          </button>
        )}
      </div>

      <div className="mt-5 flex gap-1.5" data-testid="careers-tabs">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`careers-tab-${k}`}
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors ${tab === k ? "bg-saffron text-white" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}>
            <Icon className="h-3.5 w-3.5" /> {l}
          </button>
        ))}
      </div>

      {/* JOBS TAB */}
      {tab === "jobs" && (
        <div className="mt-5" data-testid="careers-jobs-panel">
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2 rounded-lg border border-adminborder bg-adminbg px-3">
              <Search className="h-3.5 w-3.5 text-slate-500" />
              <input className="bg-transparent py-2 text-sm text-slate-200 outline-none" placeholder="Search jobs…" data-testid="careers-jobs-search"
                     value={jobFilter.q} onChange={(e) => setJobFilter({ ...jobFilter, q: e.target.value })} />
            </div>
            <select className={`${darkInput} max-w-36`} data-testid="careers-jobs-status-filter" value={jobFilter.status} onChange={(e) => setJobFilter({ ...jobFilter, status: e.target.value })}>
              <option value="">All Status</option><option value="draft">Draft</option><option value="published">Published</option><option value="closed">Closed</option>
            </select>
          </div>
          <div className="mt-4 overflow-x-auto rounded-xl border border-adminborder bg-admincard">
            <table className="w-full text-sm" data-testid="careers-jobs-table">
              <thead>
                <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Job ID</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Department</th>
                  <th className="px-4 py-3">Location</th><th className="px-4 py-3">Apps</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-b border-adminborder/60 last:border-0 hover:bg-white/5" data-testid={`careers-job-row-${j.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-saffron">{j.job_id}</td>
                    <td className="px-4 py-3 font-semibold text-white">{j.title}{j.featured ? <span className="ml-1.5 rounded bg-saffron/15 px-1.5 py-0.5 text-[9px] font-bold text-saffron">FEATURED</span> : null}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{j.department}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{j.location}</td>
                    <td className="px-4 py-3 text-xs font-bold text-slate-300">{j.applications ?? 0}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${j.status === "published" ? "bg-emerald-500/15 text-emerald-400" : j.status === "closed" ? "bg-red-500/15 text-red-400" : "bg-slate-500/15 text-slate-400"}`}>{j.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        <button onClick={() => setJobForm({ ...j, responsibilities: toLines(j.responsibilities), requirements: toLines(j.requirements), qualifications: toLines(j.qualifications), skills: (j.skills || []).join(", "), benefits: toLines(j.benefits) })}
                                data-testid={`careers-job-edit-${j.id}`} className="rounded-lg border border-adminborder p-1.5 text-slate-300 hover:border-saffron hover:text-saffron"><Edit3 className="h-3.5 w-3.5" /></button>
                        <button onClick={() => jobAction(j, "duplicate")} data-testid={`careers-job-dup-${j.id}`} className="rounded-lg border border-adminborder p-1.5 text-slate-300 hover:border-blue-400 hover:text-blue-400"><Copy className="h-3.5 w-3.5" /></button>
                        {j.status !== "published" && <button onClick={() => jobAction(j, "published")} data-testid={`careers-job-publish-${j.id}`} className="rounded-lg bg-emerald-500/10 px-2 py-1.5 text-[10px] font-bold text-emerald-400 hover:bg-emerald-500/20">Publish</button>}
                        {j.status === "published" && <button onClick={() => jobAction(j, "closed")} data-testid={`careers-job-close-${j.id}`} className="rounded-lg bg-amber-500/10 px-2 py-1.5 text-[10px] font-bold text-amber-400 hover:bg-amber-500/20">Close</button>}
                        <button onClick={() => jobAction(j, "delete")} data-testid={`careers-job-delete-${j.id}`} className="rounded-lg bg-red-500/10 p-1.5 text-red-400 hover:bg-red-500/20"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!jobs.length && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No jobs yet — create your first opening.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* APPLICATIONS TAB */}
      {tab === "applications" && (
        <div className="mt-5" data-testid="careers-apps-panel">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7" data-testid="careers-apps-metrics">
            {[["total", "Total"], ...APP_STATUS.map((s) => [s, APP_LABELS[s]])].map(([k, l]) => (
              <div key={k} className="rounded-xl border border-adminborder bg-admincard p-3.5 text-center" data-testid={`careers-metric-${k}`}>
                <p className="font-heading text-xl font-extrabold text-white">{apps.metrics[k] ?? 0}</p>
                <p className="mt-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-500">{l}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <div className="flex items-center gap-2 rounded-lg border border-adminborder bg-adminbg px-3">
              <Search className="h-3.5 w-3.5 text-slate-500" />
              <input className="bg-transparent py-2 text-sm text-slate-200 outline-none" placeholder="Search name / email / ID…" data-testid="careers-apps-search"
                     value={appFilter.q} onChange={(e) => setAppFilter({ ...appFilter, q: e.target.value })} />
            </div>
            <select className={`${darkInput} max-w-40`} data-testid="careers-apps-status-filter" value={appFilter.status} onChange={(e) => setAppFilter({ ...appFilter, status: e.target.value })}>
              <option value="">All Stages</option>{APP_STATUS.map((s) => <option key={s} value={s}>{APP_LABELS[s]}</option>)}
            </select>
          </div>
          <div className="mt-4 space-y-3" data-testid="careers-apps-list">
            {apps.items.map((a) => (
              <button key={a.id} onClick={() => setOpenApp(a)} data-testid={`careers-app-${a.id}`}
                      className="flex w-full flex-wrap items-center gap-3 rounded-xl border border-adminborder bg-admincard p-4 text-left transition-colors hover:border-saffron/40">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-white">{a.name} <span className="ml-1 font-mono text-[10px] font-normal text-slate-500">{a.application_id}</span></p>
                  <p className="mt-0.5 text-[11px] text-slate-400">{a.job_title} · {a.city} · {a.experience_years || "—"}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${APP_COLORS[a.status] || APP_COLORS.new}`}>{APP_LABELS[a.status] || a.status}</span>
              </button>
            ))}
            {!apps.items.length && <div className="rounded-xl border border-adminborder bg-admincard p-12 text-center text-sm text-slate-500">No applications yet.</div>}
          </div>
        </div>
      )}

      {/* TALENT TAB */}
      {tab === "talent" && (
        <div className="mt-5" data-testid="careers-talent-panel">
          <div className="flex items-center gap-2 rounded-lg border border-adminborder bg-adminbg px-3 sm:max-w-sm">
            <Search className="h-3.5 w-3.5 text-slate-500" />
            <input className="w-full bg-transparent py-2 text-sm text-slate-200 outline-none" placeholder="Search name / email / skills…" data-testid="careers-talent-search"
                   value={talentQ} onChange={(e) => setTalentQ(e.target.value)} />
          </div>
          <div className="mt-4 grid gap-3 lg:grid-cols-2" data-testid="careers-talent-list">
            {talent.map((t) => (
              <div key={t.id} className="rounded-xl border border-adminborder bg-admincard p-4" data-testid={`careers-talent-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-bold text-white">{t.name}</p>
                    <p className="mt-0.5 text-[11px] text-slate-400">{t.email} · {t.phone}</p>
                  </div>
                  <button onClick={async () => { if (!window.confirm(`Remove ${t.name}?`)) return; await api.delete(`/careers/admin/talent/${t.id}`); loadTalent(); }}
                          data-testid={`careers-talent-delete-${t.id}`} className="rounded-lg bg-red-500/10 p-1.5 text-red-400 hover:bg-red-500/20"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] font-semibold text-slate-400">
                  {t.department && <span className="rounded bg-saffron/10 px-2 py-0.5 text-saffron">{t.department}</span>}
                  {t.location && <span className="rounded bg-white/5 px-2 py-0.5">{t.location}</span>}
                  {t.experience && <span className="rounded bg-white/5 px-2 py-0.5">{t.experience}</span>}
                  {(t.skills || []).map((s) => <span key={s} className="rounded bg-white/5 px-2 py-0.5">{s}</span>)}
                </div>
                {t.message && <p className="mt-2 line-clamp-2 text-[11px] text-slate-500">{t.message}</p>}
              </div>
            ))}
            {!talent.length && <div className="rounded-xl border border-adminborder bg-admincard p-12 text-center text-sm text-slate-500 lg:col-span-2">No talent community profiles yet.</div>}
          </div>
        </div>
      )}

      {/* CONTENT TAB */}
      {tab === "content" && cms && (
        <div className="mt-5 max-w-3xl space-y-5" data-testid="careers-content-panel">
          <div className="rounded-xl border border-adminborder bg-admincard p-5">
            <h3 className="font-heading text-base font-bold text-white">Hero & Culture Media</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Hero image path (upload via Media Library)</label>
                <input className={darkInput} data-testid="cms-hero-path" placeholder="mediahub/….webp" value={cms.hero_image_path || ""} onChange={(e) => setCms({ ...cms, hero_image_path: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Culture video path</label>
                <input className={darkInput} data-testid="cms-culture-path" placeholder="uploads/….mp4" value={cms.culture_video_path || ""} onChange={(e) => setCms({ ...cms, culture_video_path: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Recruitment email</label>
                <input className={darkInput} data-testid="cms-recruit-email" value={cms.recruitment_email || ""} onChange={(e) => setCms({ ...cms, recruitment_email: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Resume max size (MB)</label>
                <input type="number" min="1" max="25" className={darkInput} data-testid="cms-resume-max" value={cms.resume_max_mb || 5} onChange={(e) => setCms({ ...cms, resume_max_mb: parseFloat(e.target.value) || 5 })} />
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-500">Life at IndiaFast gallery & team videos come from the Media Library (Team IndiaFast / Behind the Scenes / Events categories).</p>
          </div>

          <div className="rounded-xl border border-adminborder bg-admincard p-5">
            <h3 className="font-heading text-base font-bold text-white">Statistics (real approved numbers only)</h3>
            {(cms.stats || []).map((s, i) => (
              <div key={i} className="mt-3 flex gap-2">
                <input className={darkInput} placeholder="Value e.g. 50+" value={s.value} data-testid={`cms-stat-value-${i}`}
                       onChange={(e) => setCms({ ...cms, stats: cms.stats.map((x, j) => j === i ? { ...x, value: e.target.value } : x) })} />
                <input className={darkInput} placeholder="Label e.g. Team Members" value={s.label} data-testid={`cms-stat-label-${i}`}
                       onChange={(e) => setCms({ ...cms, stats: cms.stats.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })} />
                <button onClick={() => setCms({ ...cms, stats: cms.stats.filter((_, j) => j !== i) })} className="rounded-lg bg-red-500/10 px-3 text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <button onClick={() => setCms({ ...cms, stats: [...(cms.stats || []), { value: "", label: "" }] })} data-testid="cms-stat-add"
                    className="mt-3 flex items-center gap-1.5 text-xs font-bold text-saffron"><Plus className="h-3.5 w-3.5" /> Add stat</button>
          </div>

          <div className="rounded-xl border border-adminborder bg-admincard p-5">
            <h3 className="font-heading text-base font-bold text-white">FAQs</h3>
            {(cms.faqs || []).map((f, i) => (
              <div key={i} className="mt-3 space-y-2 rounded-lg border border-adminborder p-3">
                <input className={darkInput} placeholder="Question" value={f.q} data-testid={`cms-faq-q-${i}`}
                       onChange={(e) => setCms({ ...cms, faqs: cms.faqs.map((x, j) => j === i ? { ...x, q: e.target.value } : x) })} />
                <textarea rows={2} className={darkInput} placeholder="Answer" value={f.a} data-testid={`cms-faq-a-${i}`}
                          onChange={(e) => setCms({ ...cms, faqs: cms.faqs.map((x, j) => j === i ? { ...x, a: e.target.value } : x) })} />
                <button onClick={() => setCms({ ...cms, faqs: cms.faqs.filter((_, j) => j !== i) })} className="text-[11px] font-bold text-red-400">Remove</button>
              </div>
            ))}
            <button onClick={() => setCms({ ...cms, faqs: [...(cms.faqs || []), { q: "", a: "" }] })} data-testid="cms-faq-add"
                    className="mt-3 flex items-center gap-1.5 text-xs font-bold text-saffron"><Plus className="h-3.5 w-3.5" /> Add FAQ</button>
          </div>

          <div className="rounded-xl border border-adminborder bg-admincard p-5">
            <h3 className="font-heading text-base font-bold text-white">Employee Stories</h3>
            {(cms.employee_stories || []).map((s, i) => (
              <div key={i} className="mt-3 grid gap-2 rounded-lg border border-adminborder p-3 sm:grid-cols-2">
                <input className={darkInput} placeholder="Name" value={s.name} onChange={(e) => setCms({ ...cms, employee_stories: cms.employee_stories.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} />
                <input className={darkInput} placeholder="Designation" value={s.designation || ""} onChange={(e) => setCms({ ...cms, employee_stories: cms.employee_stories.map((x, j) => j === i ? { ...x, designation: e.target.value } : x) })} />
                <input className={darkInput} placeholder="Department" value={s.department || ""} onChange={(e) => setCms({ ...cms, employee_stories: cms.employee_stories.map((x, j) => j === i ? { ...x, department: e.target.value } : x) })} />
                <input className={darkInput} placeholder="Location" value={s.location || ""} onChange={(e) => setCms({ ...cms, employee_stories: cms.employee_stories.map((x, j) => j === i ? { ...x, location: e.target.value } : x) })} />
                <input className={darkInput} placeholder="Years (e.g. 3 Years)" value={s.years || ""} onChange={(e) => setCms({ ...cms, employee_stories: cms.employee_stories.map((x, j) => j === i ? { ...x, years: e.target.value } : x) })} />
                <input className={darkInput} placeholder="Photo path (via Media Library)" value={s.photo_path || ""} onChange={(e) => setCms({ ...cms, employee_stories: cms.employee_stories.map((x, j) => j === i ? { ...x, photo_path: e.target.value } : x) })} />
                <textarea rows={2} className={`${darkInput} sm:col-span-2`} placeholder="Short story" value={s.story || ""} onChange={(e) => setCms({ ...cms, employee_stories: cms.employee_stories.map((x, j) => j === i ? { ...x, story: e.target.value } : x) })} />
                <button onClick={() => setCms({ ...cms, employee_stories: cms.employee_stories.filter((_, j) => j !== i) })} className="text-left text-[11px] font-bold text-red-400 sm:col-span-2">Remove story</button>
              </div>
            ))}
            <button onClick={() => setCms({ ...cms, employee_stories: [...(cms.employee_stories || []), { name: "", designation: "", department: "", location: "", years: "", photo_path: "", story: "" }] })}
                    data-testid="cms-story-add" className="mt-3 flex items-center gap-1.5 text-xs font-bold text-saffron"><Plus className="h-3.5 w-3.5" /> Add story</button>
          </div>

          <button onClick={saveCms} disabled={saving} data-testid="cms-save-btn"
                  className="flex items-center gap-2 rounded-lg bg-saffron px-6 py-2.5 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Careers Content
          </button>
        </div>
      )}

      {/* JOB FORM MODAL */}
      {jobForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4" data-testid="careers-job-form-modal">
          <div className="mx-auto my-8 w-full max-w-2xl rounded-xl border border-adminborder bg-admincard p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-white">{jobForm.id ? `Edit ${jobForm.job_id}` : "Create Job"}</h3>
              <button onClick={() => setJobForm(null)} className="grid h-8 w-8 place-items-center rounded-lg bg-white/5 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input className={darkInput} data-testid="job-form-title" placeholder="Job Title *" value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} />
              <input className={darkInput} data-testid="job-form-location" placeholder="Location * (e.g. Hyderabad)" value={jobForm.location} onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })} />
              <select className={darkInput} data-testid="job-form-department" value={jobForm.department} onChange={(e) => setJobForm({ ...jobForm, department: e.target.value })}>
                {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select className={darkInput} data-testid="job-form-type" value={jobForm.employment_type} onChange={(e) => setJobForm({ ...jobForm, employment_type: e.target.value })}>
                {EMP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input className={darkInput} data-testid="job-form-experience" placeholder="Experience (e.g. 1–3 Years)" value={jobForm.experience} onChange={(e) => setJobForm({ ...jobForm, experience: e.target.value })} />
              <input className={darkInput} data-testid="job-form-salary" placeholder="Salary / CTC (optional)" value={jobForm.salary} onChange={(e) => setJobForm({ ...jobForm, salary: e.target.value })} />
              <textarea rows={3} className={`${darkInput} sm:col-span-2`} data-testid="job-form-description" placeholder="About the role" value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} />
              {[["responsibilities", "Key Responsibilities (one per line)"], ["requirements", "Requirements (one per line)"], ["qualifications", "Qualifications (one per line)"], ["benefits", "What We Offer (one per line)"]].map(([k, ph]) => (
                <textarea key={k} rows={3} className={`${darkInput} sm:col-span-2`} data-testid={`job-form-${k}`} placeholder={ph} value={jobForm[k]} onChange={(e) => setJobForm({ ...jobForm, [k]: e.target.value })} />
              ))}
              <input className={`${darkInput} sm:col-span-2`} data-testid="job-form-skills" placeholder="Skills (comma separated)" value={jobForm.skills} onChange={(e) => setJobForm({ ...jobForm, skills: e.target.value })} />
              <input type="date" className={darkInput} data-testid="job-form-deadline" value={jobForm.deadline} onChange={(e) => setJobForm({ ...jobForm, deadline: e.target.value })} />
              <input className={darkInput} data-testid="job-form-contact" placeholder="Recruitment contact email" value={jobForm.contact_email} onChange={(e) => setJobForm({ ...jobForm, contact_email: e.target.value })} />
              <div className="flex flex-wrap gap-4 sm:col-span-2">
                {[["show_salary", "Show salary publicly"], ["featured", "Featured job"]].map(([k, l]) => (
                  <label key={k} className="flex cursor-pointer items-center gap-2 text-xs font-bold text-slate-300" data-testid={`job-form-${k}`}>
                    <input type="checkbox" checked={!!jobForm[k]} onChange={(e) => setJobForm({ ...jobForm, [k]: e.target.checked })} className="h-4 w-4 accent-saffron" /> {l}
                  </label>
                ))}
                <select className={`${darkInput} max-w-40`} data-testid="job-form-status" value={jobForm.status} onChange={(e) => setJobForm({ ...jobForm, status: e.target.value })}>
                  <option value="draft">Draft</option><option value="published">Published</option><option value="closed">Closed</option>
                </select>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setJobForm(null)} className="rounded-lg border border-adminborder px-4 py-2 text-sm font-bold text-slate-300">Cancel</button>
              <button onClick={saveJob} disabled={saving} data-testid="job-form-save"
                      className="flex items-center gap-2 rounded-lg bg-saffron px-5 py-2 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {jobForm.id ? "Save Changes" : "Create Job"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* APPLICATION DETAIL MODAL */}
      {openApp && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4" data-testid="careers-app-modal">
          <div className="mx-auto my-8 w-full max-w-xl rounded-xl border border-adminborder bg-admincard p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[11px] text-saffron">{openApp.application_id}</p>
                <h3 className="mt-1 font-heading text-lg font-bold text-white">{openApp.name}</h3>
                <p className="text-xs text-slate-400">{openApp.job_title} · applied {(openApp.created_at || "").slice(0, 10)}</p>
              </div>
              <button onClick={() => setOpenApp(null)} className="grid h-8 w-8 place-items-center rounded-lg bg-white/5 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs" data-testid="careers-app-details">
              {[["Phone", openApp.phone], ["Email", openApp.email], ["City", openApp.city], ["Location", openApp.current_location],
                ["Experience", openApp.experience_years], ["Company", openApp.current_company], ["Title", openApp.current_title],
                ["Current CTC", openApp.current_ctc], ["Expected CTC", openApp.expected_ctc], ["Notice", openApp.notice_period],
                ["Qualification", openApp.qualification]].map(([k, v]) => v ? (
                <p key={k}><span className="font-bold uppercase tracking-wider text-slate-500">{k}: </span><span className="text-slate-300">{v}</span></p>
              ) : null)}
            </div>
            {!!(openApp.skills || []).length && (
              <div className="mt-3 flex flex-wrap gap-1.5">{(openApp.skills || []).map((s) => <span key={s} className="rounded bg-white/5 px-2 py-0.5 text-[10px] font-semibold text-slate-300">{s}</span>)}</div>
            )}
            {openApp.message && <p className="mt-3 rounded-lg bg-adminbg p-3 text-xs text-slate-400">{openApp.message}</p>}
            <div className="mt-4 flex flex-wrap gap-2">
              {openApp.linkedin && <a href={openApp.linkedin} target="_blank" rel="noreferrer" className="rounded-lg border border-adminborder px-3 py-1.5 text-[11px] font-bold text-blue-400">LinkedIn</a>}
              {openApp.portfolio && <a href={openApp.portfolio} target="_blank" rel="noreferrer" className="rounded-lg border border-adminborder px-3 py-1.5 text-[11px] font-bold text-blue-400">Portfolio</a>}
              <button onClick={() => downloadResume(openApp)} data-testid="careers-app-resume"
                      className="flex items-center gap-1.5 rounded-lg border border-saffron/40 bg-saffron/10 px-3 py-1.5 text-[11px] font-bold text-saffron hover:bg-saffron/20">
                <Download className="h-3.5 w-3.5" /> Resume
              </button>
            </div>
            <p className="mb-2 mt-5 text-[10px] font-bold uppercase tracking-wider text-slate-500">Recruitment Pipeline</p>
            <div className="flex flex-wrap gap-1.5" data-testid="careers-app-pipeline">
              {APP_STATUS.map((s) => (
                <button key={s} onClick={() => setAppStatus(openApp, s)} data-testid={`careers-app-status-${s}`}
                        className={`rounded-full px-3 py-1.5 text-[10px] font-bold transition-colors ${openApp.status === s ? APP_COLORS[s] + " ring-1 ring-current" : "bg-white/5 text-slate-500 hover:bg-white/10"}`}>
                  {APP_LABELS[s]}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
